/**
 * Authentication Context
 * ======================
 * Provides authentication state and methods throughout the app
 */

import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase, getUserRole, getUserProfile } from '../services/supabaseClient';
import { 
  signIn as authSignIn, 
  signUp as authSignUp,
  signOut as authSignOut,
  signInWithProvider as authSignInWithProvider
} from '../services/authService';
import { PageLoader } from '../utils/lazyLoad';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);

  useEffect(() => {
    let mounted = true;

    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, 'Session:', session ? 'Present' : 'None');
        
        if (!mounted) return;
        
        if (session?.user) {
          console.log('Setting session and user from listener');
          setSession(session);
          setUser(session.user);
          
          // Get role and profile
          try {
            console.log('Getting role from listener...');
            const role = await Promise.race([
              getUserRole(session.user.id),
              new Promise((_, reject) => setTimeout(() => reject(new Error('Role check timeout')), 5000))
            ]);
            console.log('Role from listener:', role);
            setRole(role);
            
            if (role) {
              console.log('Getting profile from listener...');
              const profile = await getUserProfile(session.user.id, role);
              console.log('Profile from listener:', profile);
              setProfile(profile);
            } else {
              setProfile(null);
            }
          } catch (roleError) {
            console.log('Role check failed in listener:', roleError.message);
            // Don't set role to null immediately, wait for INITIAL_SESSION
            if (event === 'INITIAL_SESSION') {
              setRole(null);
              setProfile(null);
            }
          }
        } else {
          console.log('Clearing auth state from listener');
          setSession(null);
          setUser(null);
          setRole(null);
          setProfile(null);
        }
        
        // Set loading to false immediately if no session, or on INITIAL_SESSION
        if (!session) {
          console.log('Setting loading to false - no session');
          setLoading(false);
        } else if (event === 'INITIAL_SESSION') {
          console.log('Setting loading to false - initial session');
          setLoading(false);
        } else if (event === 'SIGNED_IN' && session?.user) {
          // For SIGNED_IN events, only wait briefly for role check
          setTimeout(() => {
            if (mounted) {
              console.log('Setting loading to false - signed in timeout');
              setLoading(false);
            }
          }, 1000); // Only wait 1 second max for SIGNED_IN
        }
      }
    );

    // Cleanup subscription
    return () => {
      mounted = false;
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  const checkUser = async () => {
    try {
      console.log('Checking user...');
      
      // First, just check if there's a session without getting role
      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error) {
        console.log('Error getting user:', error);
        throw error;
      }
      
      if (user) {
        console.log('User found:', user.email);
        setUser(user);
        
        // Try to get role with a timeout
        try {
          console.log('Getting user role...');
          const role = await Promise.race([
            getUserRole(user.id),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Role check timeout')), 5000))
          ]);
          console.log('User role:', role);
          setRole(role);
          
          if (role) {
            console.log('Getting user profile...');
            const profile = await getUserProfile(user.id, role);
            console.log('User profile:', profile);
            setProfile(profile);
          }
        } catch (roleError) {
          console.log('Role check failed or timed out:', roleError.message);
          setRole(null);
          setProfile(null);
        }
      } else {
        console.log('No user found');
        setUser(null);
        setRole(null);
        setProfile(null);
      }
    } catch (error) {
      console.error('Error checking user:', error);
      setUser(null);
      setRole(null);
      setProfile(null);
    } finally {
      console.log('Setting loading to false - checkUser complete');
      setLoading(false);
    }
  };

  const signUp = async (email, password, additionalData) => {
    setLoading(true);
    const result = await authSignUp(email, password, additionalData);
    setLoading(false);
    
    if (result.success) {
      // Note: User might need to verify email before they can sign in
      // So we don't automatically set them as authenticated
      return result;
    }
    return result;
  };

  const signIn = async (email, password) => {
    setLoading(true);
    const result = await authSignIn(email, password);
    
    if (result.success) {
      setUser(result.user);
      setSession(result.session);
      setRole(result.role);
      setProfile(result.profile);
    }
    
    setLoading(false);
    return result;
  };

  const signInWithProvider = async (provider) => {
    setLoading(true);
    const result = await authSignInWithProvider(provider);
    setLoading(false);
    return result;
  };

  const signOut = async () => {
    setLoading(true);
    const result = await authSignOut();
    
    if (result.success) {
      setUser(null);
      setSession(null);
      setRole(null);
      setProfile(null);
    }
    
    setLoading(false);
    return result;
  };

  const getAccessToken = () => {
    return session?.access_token || null;
  };

  const isAuthenticated = () => {
    return !!user && !!session;
  };

  const hasRole = (requiredRole) => {
    if (Array.isArray(requiredRole)) {
      return requiredRole.includes(role);
    }
    return role === requiredRole;
  };

  const value = {
    user,
    role,
    profile,
    session,
    loading,
    signUp,
    signIn,
    signInWithProvider,
    signOut,
    getAccessToken,
    isAuthenticated,
    hasRole,
    refreshUser: checkUser
  };

  return (
    <AuthContext.Provider value={value}>
      <div style={{
        opacity: loading ? 1 : 1,
        transition: 'opacity 0.3s ease-in-out'
      }}>
        {loading ? (
          <div style={{
            height: '100vh',
            background: '#121212',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999
          }}>
            <PageLoader />
          </div>
        ) : (
          children
        )}
      </div>
    </AuthContext.Provider>
  );
};

export default AuthContext;

